/**
 * The full-window wash shown while the reader is waiting on a request.
 *
 * The element itself is rendered once in index.js and never unmounted; these functions are what the
 * axios interceptors there call to raise and lower it. Kept out of index.js because that module mounts
 * the application as it is evaluated and so cannot be imported by a test, and because the rule about
 * which requests may hold the screen up is worth being able to state in one.
 */

/** The id of the element index.js renders for the wash, shared so the two cannot drift apart. */
export const LOADING_WASH_ELEMENT_ID = "centerLoader";

/**
 * Whether a request should hold the screen up while it is in flight.
 *
 * A refresh nobody asked for must not. The processing page re-reads its first page on a timer and its
 * counters ask for twelve more figures alongside; washing the entire window out white for the couple of
 * hundred milliseconds those take turned a page quietly keeping itself current into one that looked
 * like it was reloading itself over and over — which is how it was reported, as a white flash every ten
 * seconds, the interval it polled on then. Callers on a timer therefore mark their requests
 * `background: true`. A request a reader is actually waiting for says nothing and is treated exactly as
 * it always was, so the default stays the safe one: silence means the reader is waiting.
 *
 * Read off the axios config so the decision travels with the request, rather than from a module
 * variable that the next caller would have to remember to set back.
 */
export const holdsUpTheScreen = (config) => !config?.background;

/*
 * How many screen-holding requests are still outstanding.
 *
 * Counted rather than toggled, because a page load fires a dozen calls at once and hiding the wash on
 * the first response to come back took it away while the other eleven were still in flight. Only the
 * last to settle lowers it. Background requests are not counted at all, so a tick that lands in the
 * middle of a genuine load cannot lower it either.
 */
let requestsHoldingScreen = 0;

/**
 * Shows or hides the wash.
 *
 * Tolerates the element being absent rather than assuming it: the interceptors are registered while
 * index.js is being evaluated, so a request made before React has mounted would otherwise throw here
 * and reject a request that was perfectly good.
 */
const setWashVisible = (visible) => {
    const wash = document.getElementById(LOADING_WASH_ELEMENT_ID);
    if (wash) wash.style.display = visible ? "block" : "none";
};

/** Raises the wash for a request the reader is waiting on. Background requests are ignored. */
export const noteRequestStarted = (config) => {
    if (!holdsUpTheScreen(config)) return;
    requestsHoldingScreen += 1;
    setWashVisible(true);
};

/**
 * Releases the hold a settled request had on the screen, whether it succeeded, failed or was aborted.
 *
 * Floored at zero so that a response with no matching start — a request already in flight when the dev
 * server reloaded the module, or one whose interceptor never ran — cannot leave the count negative and
 * strand the wash on screen for the rest of the session.
 */
export const noteRequestSettled = (config) => {
    if (!holdsUpTheScreen(config)) return;
    requestsHoldingScreen = Math.max(0, requestsHoldingScreen - 1);
    if (requestsHoldingScreen === 0) setWashVisible(false);
};
